import React, { useState, useEffect, useRef } from "react";
import {
  Mail, Lock, User, MapPin, Wrench, ArrowRight, ShieldCheck,
  Building2, Phone, Briefcase, Car, Sun, Moon, Check, Eye, EyeOff,
  Loader2, ArrowLeft, ChevronRight
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
// Adjust import path if needed
import Disclaimer from "../Legal/Disclaimer"; 

// Securely pull API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ── Design Tokens & Hooks ─────────────────────────────────────────────────
function useTheme() {
  const [theme, setTheme] = useState(
    () => typeof window !== "undefined" ? localStorage.getItem("theme") || "light" : "light"
  );
  useEffect(() => {
    const root = window.document.documentElement;
    theme === "dark" ? root.classList.add("dark") : root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme]);
  return [theme, () => setTheme(t => t === "light" ? "dark" : "light")];
}

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
].map(s => ({ value: s.slice(0,2).toUpperCase(), label: s }));

const CY = new Date().getFullYear();
// Dealer-style year range: next model year down through 1990
const VEHICLE_YEARS  = Array.from({ length: (CY + 1) - 1990 + 1 }, (_, i) => String((CY + 1) - i));

// Comprehensive make list, similar in spirit to CarMax / VinAudit style pickers
const VEHICLE_MAKES  = [
  "Acura","Audi","BMW","Buick","Cadillac","Chevrolet","Chrysler","Dodge",
  "Ford","Freightliner","GMC","Honda","Hyundai","Infiniti","International",
  "Isuzu","Jeep","Kenworth","Kia","Land Rover","Lexus","Lincoln","Mack",
  "Mazda","Mercedes-Benz","Mitsubishi","Nissan","Peterbilt","RAM","Subaru",
  "Tesla","Toyota","Volkswagen","Volvo","Western Star","Other",
].sort((a,b) => a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b));

// Popular model lists per make — driver can also type a custom model if theirs isn't listed
const VEHICLE_MODELS = {
  "Acura":        ["MDX","RDX","TLX","ILX","Integra","NSX","RLX","ZDX","Other"],
  "Audi":         ["A3","A4","A5","A6","A7","A8","Q3","Q5","Q7","Q8","e-tron","Other"],
  "BMW":          ["2 Series","3 Series","4 Series","5 Series","7 Series","X1","X3","X5","X7","Z4","Other"],
  "Buick":        ["Enclave","Encore","Envision","LaCrosse","Regal","Other"],
  "Cadillac":     ["CT4","CT5","Escalade","XT4","XT5","XT6","Other"],
  "Chevrolet":    ["Silverado 1500","Silverado 2500HD","Silverado 3500HD","Colorado","Tahoe","Suburban","Express Van","Equinox","Traverse","Blazer","Malibu","Camaro","Trax","Other"],
  "Chrysler":     ["300","Pacifica","Voyager","Other"],
  "Dodge":        ["Charger","Challenger","Durango","Journey","Grand Caravan","Other"],
  "Ford":         ["F-150","F-250 Super Duty","F-350 Super Duty","F-450 Super Duty","F-550 Super Duty","Explorer","Expedition","Transit Van","Transit Connect","E-Series Van","Ranger","Edge","Escape","Bronco","Other"],
  "Freightliner": ["M2 106","Cascadia","Sprinter","Business Class","Other"],
  "GMC":          ["Sierra 1500","Sierra 2500HD","Sierra 3500HD","Canyon","Yukon","Yukon XL","Savana Van","Acadia","Terrain","Other"],
  "Honda":        ["Ridgeline","Pilot","Passport","CR-V","Odyssey","Civic","Accord","HR-V","Other"],
  "Hyundai":      ["Santa Fe","Palisade","Tucson","Sonata","Elantra","Other"],
  "Infiniti":     ["QX50","QX60","QX80","Q50","Other"],
  "International":["MXT","CXT","CV Series","HX Series","DuraStar","Other"],
  "Isuzu":        ["NPR","NPR-HD","NQR","FTR","Other"],
  "Jeep":         ["Grand Cherokee","Cherokee","Wrangler","Gladiator","Compass","Renegade","Other"],
  "Kenworth":     ["T680","T880","W900","T370","Other"],
  "Kia":          ["Sorento","Telluride","Sportage","Soul","K5","Other"],
  "Land Rover":   ["Range Rover","Range Rover Sport","Discovery","Defender","Other"],
  "Lexus":        ["RX","GX","LX","NX","ES","Other"],
  "Lincoln":      ["Navigator","Aviator","Corsair","Nautilus","Other"],
  "Mack":         ["Anthem","Granite","Pinnacle","Other"],
  "Mazda":        ["CX-5","CX-9","CX-30","Mazda3","Mazda6","Other"],
  "Mercedes-Benz":["Sprinter","GLE","GLS","GLC","Metris","Other"],
  "Mitsubishi":   ["Outlander","Eclipse Cross","Mirage","Other"],
  "Nissan":       ["Titan","Titan XD","Frontier","Armada","Pathfinder","Murano","NV Cargo Van","NV200","Other"],
  "Peterbilt":    ["579","389","567","337","Other"],
  "RAM":          ["1500","2500","3500","4500","5500","ProMaster Van","ProMaster City","Other"],
  "Subaru":       ["Outback","Forester","Ascent","Crosstrek","Other"],
  "Tesla":        ["Model 3","Model Y","Model S","Model X","Cybertruck","Other"],
  "Toyota":       ["Tundra","Tacoma","4Runner","Sequoia","Highlander","Sienna","RAV4","Land Cruiser","Other"],
  "Volkswagen":   ["Atlas","Tiguan","Taos","ID.4","Other"],
  "Volvo":        ["XC90","XC60","XC40","VNL Series","Other"],
  "Western Star": ["4700","4900","5700","Other"],
};
const VEHICLE_COLORS = [
  "White","Black","Silver","Gray","Dark Gray","Red","Blue","Dark Blue","Navy",
  "Green","Yellow","Orange","Beige","Brown","Gold","Other",
];

const ROLES = [
  {
    id: "driver",
    icon: "🚛",
    title: "Pilot Car Driver",
    subtitle: "Get certified, find loads & get hired",
    color: "#f59e0b",
    colorL: "#fbbf24",
    perks: ["Verified driver badge", "Access live load board", "Equipment marketplace", "Invoice builder"],
  },
  {
    id: "company",
    icon: "🏢",
    title: "Carrier / Company / Broker",
    subtitle: "Post loads & find certified drivers",
    color: "#3b82f6",
    colorL: "#60a5fa",
    perks: ["Post unlimited loads", "Search certified drivers", "1-click cert verify", "Receive invoices"],
  },
  {
    id: "dispatcher",
    icon: "🎧",
    title: "Dispatcher",
    subtitle: "Manage escorts & coordinate teams",
    color: "#8b5cf6",
    colorL: "#a78bfa",
    perks: ["Live GPS team tracking", "AI dispatch console", "Real-time job status", "Escort coordination"],
  },
];

// ── OTP Screen Component ──────────────────────────────────────────────────
function OtpScreen({ email, onBack, colors }) {
  const [otp, setOtp]             = useState(Array(6).fill(""));
  const [isVerifying, setVerifying] = useState(false);
  const [isResending, setResending] = useState(false);
  const [apiError, setApiError]   = useState("");
  const [apiSuccess, setSuccess]  = useState("");
  const refs = useRef([]);
  const navigate = useNavigate();
  const { text, textMid, border, inputBg, amber, amberL, panelBg } = colors;

  const change = (el, i) => {
    if (isNaN(el.value)) return;
    const n = [...otp]; n[i] = el.value; setOtp(n); setApiError("");
    if (el.nextSibling && el.value) el.nextSibling.focus();
  };
  
  const keydown = (e, i) => {
    if (e.key === "Backspace" && !otp[i] && refs.current[i-1]) refs.current[i-1].focus();
  };
  
  const paste = (e) => {
    e.preventDefault();
    const d = e.clipboardData.getData("text").slice(0,6).split("");
    if (d.some(c => isNaN(c))) return;
    const n = [...otp]; d.forEach((v,i) => n[i]=v); setOtp(n);
    refs.current[Math.min(d.length,5)].focus();
  };

  const verify = async (e) => {
    e.preventDefault(); 
    setApiError(""); 
    setVerifying(true);
    const code = otp.join("");
    
    if (code.length !== 6) { 
      setApiError("Enter all 6 digits."); 
      setVerifying(false); 
      return; 
    }
    
    try {
      const verifyRes = await fetch(`${API_BASE_URL}/api/verify-otp/`, {
        method:"POST", 
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email, otp: code }),
      });
      const verifyData = await verifyRes.json();
      
      if (verifyRes.ok) { 
        setSuccess("Account verified! Setting up your trial...");
        
        if (verifyData.token) {
          localStorage.setItem("authToken", verifyData.token);
        }
        
        const checkoutRes = await fetch(`${API_BASE_URL}/api/payments/create-checkout-session/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${verifyData.token}`
          },
          body: JSON.stringify({})
        });
        
        const checkoutData = await checkoutRes.json();
        
        if (checkoutRes.ok && checkoutData.url) {
          window.location.href = checkoutData.url;
        } else {
          setApiError("Verified, but failed to load billing. Please log in.");
          setTimeout(() => navigate("/login"), 2000);
        }
      } else { 
        setApiError(verifyData.message || "Incorrect code. Try again."); 
        setOtp(Array(6).fill("")); 
        refs.current[0]?.focus(); 
      }
    } catch { 
      setApiError("Connection error."); 
    } finally { 
      setVerifying(false); 
    }
  };

  const resend = async () => {
    setApiError(""); setSuccess(""); setResending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/resend-otp/`, {
        method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess("New code sent!"); setOtp(Array(6).fill("")); refs.current[0]?.focus(); }
      else setApiError(data.message || "Failed to resend.");
    } catch { setApiError("Connection error."); }
    finally { setResending(false); }
  };

  const inpStyles = {
    height:56, width:48, borderRadius:12, border:`2px solid ${border}`,
    background:inputBg, color:text, fontSize:22, fontWeight:800,
    textAlign:"center", outline:"none", fontFamily:"inherit", transition:"border-color .15s",
  };

  return (
    <div style={{ textAlign:"center", padding:"8px 0" }}>
      <div style={{ width:72, height:72, borderRadius:"50%", background:`${amber}15`, border:`2px solid ${amber}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, margin:"0 auto 20px" }}>
        ✉️
      </div>
      <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:24, fontWeight:900, color:text, marginBottom:8 }}>Check your inbox</h2>
      <p style={{ fontSize:14, color:textMid, marginBottom:28 }}>
        We sent a 6-digit code to <strong style={{ color:text }}>{email}</strong>
      </p>

      {apiError && <div style={{ marginBottom:16, padding:"10px 14px", borderRadius:10, background:"rgba(251,113,133,0.12)", border:"1px solid rgba(251,113,133,0.30)", color:"#fda4af", fontSize:13, fontWeight:500 }}>⚠ {apiError}</div>}
      {apiSuccess && <div style={{ marginBottom:16, padding:"10px 14px", borderRadius:10, background:"rgba(52,211,153,0.12)", border:"1px solid rgba(52,211,153,0.30)", color:"#34d399", fontSize:13, fontWeight:600 }}>✓ {apiSuccess}</div>}

      <form onSubmit={verify}>
        <div style={{ display:"flex", justifyContent:"center", gap:10, marginBottom:28 }} onPaste={paste}>
          {otp.map((v,i) => (
            <input key={i} type="text" maxLength="1" value={v}
              ref={el => refs.current[i]=el}
              onChange={e => change(e.target, i)}
              onKeyDown={e => keydown(e, i)}
              onFocus={e => e.target.select()}
              style={{ ...inpStyles, borderColor: v ? amber : border }}
            />
          ))}
        </div>
        <button type="submit" disabled={isVerifying || otp.join("").length!==6}
          style={{ width:"100%", padding:"14px", borderRadius:12, border:"none", cursor:"pointer",
            background:isVerifying||otp.join("").length!==6?"rgba(245,158,11,0.4)":`linear-gradient(135deg,${amber},${amberL})`,
            color:"#0d1f3c", fontWeight:800, fontSize:15, fontFamily:"inherit",
            boxShadow:"0 4px 16px rgba(245,158,11,0.35)", marginBottom:20,
          }}>
          {isVerifying ? "Verifying…" : "Verify & Start Trial →"}
        </button>
      </form>

      <p style={{ fontSize:13, color:textMid, marginBottom:8 }}>
        Didn't get the email?{" "}
        <button onClick={resend} disabled={isResending} style={{ background:"none", border:"none", cursor:"pointer", color:amber, fontWeight:700, fontSize:13, fontFamily:"inherit" }}>
          {isResending ? "Sending…" : "Resend code"}
        </button>
      </p>
      <button onClick={onBack} style={{ background:"none", border:"none", cursor:"pointer", color:textMid, fontSize:13, fontFamily:"inherit", display:"flex", alignItems:"center", gap:5, margin:"0 auto" }}>
        <ArrowLeft size={13}/> Use a different email
      </button>
    </div>
  );
}

// ── Shared Field Component ────────────────────────────────────────────────
function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:"block", fontSize:12, fontWeight:700, color:"inherit", marginBottom:6, opacity:.75, letterSpacing:".03em" }}>{label}</label>
      {children}
      {error && <p style={{ marginTop:5, fontSize:12, color:"#fda4af", fontWeight:500 }}>{error}</p>}
    </div>
  );
}

// ── Main Signup Component ─────────────────────────────────────────────────
export default function Signup() {
  const [theme, toggleTheme] = useTheme();
  const D = theme === "dark";

  const [selectedRole, setSelectedRole] = useState(null);
  const [showOtp,      setShowOtp]      = useState(false);
  const [regEmail,     setRegEmail]     = useState("");
  const [agreedToTerms,setAgreed]       = useState(false);
  const [isLoading,    setLoading]      = useState(false);
  const [apiError,     setApiError]     = useState("");
  const [errors,       setErrors]       = useState({});

  const [formData, setFormData] = useState({
    fullName:"", email:"", phone:"", companyName:"", contactName:"",
    experience:"", primaryState:"", equipmentStatus:"",
    vehicleYear:"", vehicleMake:"", vehicleModel:"", vehicleColor:"", vehicleNotes:"",
    fleetSize:"", dotNumber:"", dispatchExp:"", avatar: null,
  });
  
  const [avatarPreview, setAvatarPreview] = useState(null);
  
  const [password,        setPassword]        = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw,          setShowPw]          = useState(false);
  const [showCpw,         setShowCpw]         = useState(false);
  const [pwFocused,       setPwFocused]       = useState(false);

  const validations = {
    length:    password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number:    /[0-9]/.test(password),
  };

  const bg      = D ? "#0d1f3c"                : "#f8fafc";
  const panelBg = D ? "#142a4d"                : "#ffffff";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8"                : "#0f172a";
  const textMid = D ? "rgba(240,244,248,0.75)" : "#475569";
  const textSoft= D ? "rgba(240,244,248,0.50)" : "#94a3b8";
  const inputBg = D ? "rgba(255,255,255,0.06)" : "#f8fafc";
  const amber   = "#f59e0b";
  const amberL  = "#fbbf24";
  const colors  = { text, textMid, border, inputBg, amber, amberL, panelBg };

  const role = ROLES.find(r => r.id === selectedRole);

  const inp = (hasErr) => ({
    width:"100%", padding:"11px 11px 11px 40px", borderRadius:11,
    border:`1.5px solid ${hasErr?"#f87171":border}`, background:inputBg, color:text,
    fontSize:14, outline:"none", fontFamily:"inherit", transition:"border-color .15s", boxSizing:"border-box",
  });

  const iconStyle = { position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:textSoft, pointerEvents:"none" };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]:value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]:"" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(p => ({ ...p, avatar: file }));
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    setApiError("");
    const errs = {};
    
    // Strict Validation
    if (!formData.fullName.trim())  errs.fullName = "Required";
    if (!formData.email.trim())     errs.email    = "Required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errs.email = "Invalid email format";
    
    if (!formData.phone.trim())     errs.phone    = "Required";

    // Driver vehicle/experience details are optional — a driver can finish signup
    // and fill these in later from their profile. Nothing here should block signup.

    if (selectedRole === "company") {
      if (!formData.companyName.trim()) errs.companyName = "Required";
    }
    
    if (!validations.length || !validations.uppercase || !validations.lowercase || !validations.number)
      errs.password = "Meet all password requirements";
      
    if (!confirmPassword)                    errs.confirmPassword = "Required";
    else if (password !== confirmPassword)   errs.confirmPassword = "Passwords do not match";
    
    if (!agreedToTerms) errs.terms = "Please agree to the terms to continue";

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      // Scroll to top of form to show error if needed
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    // Split full name into first/last. If the person only typed one word (e.g. "Koie"),
    // we must NOT send an empty last_name — the backend rejects blank required fields
    // with a generic "This field may not be blank" error. Fall back to the first name.
    const nameParts = formData.fullName.trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || "";
    const lastName  = nameParts.length > 1 ? nameParts.slice(1).join(" ") : firstName;

    const payload = new FormData();
    payload.append("email", formData.email);
    payload.append("password", password);
    payload.append("first_name", firstName);
    payload.append("last_name", lastName);
    payload.append("role", selectedRole === "driver" ? "pilot" : selectedRole);
    payload.append("phone", formData.phone);
    
    if (selectedRole === "driver") {
      payload.append("experience", formData.experience || "");
      payload.append("primaryState", formData.primaryState || "");
      payload.append("equipmentStatus", formData.equipmentStatus || "");
      payload.append("vehicleYear", formData.vehicleYear || "");
      payload.append("vehicleMake", formData.vehicleMake || "");
      payload.append("vehicleModel", formData.vehicleModel || "");
      payload.append("vehicleColor", formData.vehicleColor || "");
      payload.append("vehicleNotes", formData.vehicleNotes || "");
    } else if (selectedRole === "company") {
      payload.append("companyName", formData.companyName || "");
      payload.append("contactName", formData.contactName || "");
      payload.append("fleetSize", formData.fleetSize || "");
      payload.append("dotNumber", formData.dotNumber || "");
    } else if (selectedRole === "dispatcher") {
      payload.append("dispatchExp", formData.dispatchExp || "");
    }

    if (formData.avatar) {
      payload.append("avatar", formData.avatar);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/signup/`, {
        method:"POST", 
        body: payload,
      });
      
      const contentType = res.headers.get("content-type");
      let data = {};
      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      }

      if (res.ok) { 
        setRegEmail(formData.email); 
        setShowOtp(true); 
      } else {
        setApiError(data.errors
          ? Object.values(data.errors).map(m => m.join(", ")).join(" | ")
          : data.message || "An error occurred during sign up.");
      }
    } catch { 
      setApiError("Connection error — please check your internet and try again."); 
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"'Inter',sans-serif", transition:"background .3s" }}>
      {/* ── Global Styles ── */}
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } } 
        @keyframes slideDown { from { opacity:0; transform:translateY(-10px) } to { opacity:1; transform:none } }
        /* Hides standard autofill backgrounds in webkit */
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px ${inputBg} inset !important;
            -webkit-text-fill-color: ${text} !important;
            transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 24px", maxWidth:860, margin:"0 auto" }}>
        <Link to="/" style={{ display:"flex", alignItems:"center", gap:7, textDecoration:"none", color:textMid, fontSize:13, fontWeight:600 }}>
          <ArrowLeft size={15}/> Back to Home
        </Link>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <Link to="/login" style={{ fontSize:13, fontWeight:600, color:textMid, textDecoration:"none" }}>Already a member? <span style={{ color:amber, fontWeight:700 }}>Sign in →</span></Link>
          <button onClick={toggleTheme} style={{ width:34, height:34, borderRadius:9, border:`1px solid ${border}`, background:inputBg, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:textMid }}>
            {D ? <Sun size={14}/> : <Moon size={14}/>}
          </button>
        </div>
      </div>

      <div style={{ maxWidth:860, margin:"0 auto", padding:"0 24px 60px" }}>

        <div style={{ textAlign:"center", marginBottom:36 }}>
          <Link to="/" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:8, marginBottom:18 }}>
            <div style={{ width:38, height:38, borderRadius:9, background:amber, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:17, color:"#0d1f3c" }}>P</div>
            <span style={{ fontSize:18, fontWeight:800, color:text }}>Pilot<span style={{ color:amber }}>Link</span> <span style={{ fontSize:10, fontWeight:600, color:textSoft }}>USA</span></span>
          </Link>
          <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(24px,4vw,32px)", fontWeight:900, color:text, marginBottom:8, lineHeight:1.2 }}>
            Join the #1 Pilot Car Platform
          </h1>
          <p style={{ fontSize:15, color:textMid }}>Select your account type to get started. It's free.</p>
        </div>

        {/* ── Role Selection ── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14, marginBottom: selectedRole ? 28 : 0 }}>
          {ROLES.map(r => {
            const active = selectedRole === r.id;
            return (
              <button key={r.id} onClick={() => { setSelectedRole(r.id); setErrors({}); setApiError(""); }}
                style={{
                  padding:"20px 18px", borderRadius:16, border:`2px solid ${active ? r.color : border}`,
                  background: active ? `${r.color}12` : panelBg,
                  cursor:"pointer", textAlign:"left", transition:"all .2s",
                  boxShadow: active ? `0 0 0 3px ${r.color}25, 0 6px 24px ${r.color}15` : "none",
                  transform: active ? "translateY(-2px)" : "none",
                }}
              >
                <div style={{ fontSize:28, marginBottom:10 }}>{r.icon}</div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:15, color:active ? r.color : text, marginBottom:4 }}>{r.title}</div>
                <div style={{ fontSize:12, color:textMid, marginBottom:12, lineHeight:1.5 }}>{r.subtitle}</div>
                <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                  {r.perks.map(p => (
                    <div key={p} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:active ? r.color : textSoft, fontWeight:500 }}>
                      <span style={{ color: active ? r.color : textSoft, fontSize:10 }}>✓</span>{p}
                    </div>
                  ))}
                </div>
                {active && (
                  <div style={{ marginTop:12, display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:700, color:r.color }}>
                    Selected <ChevronRight size={11}/>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Form Section ── */}
        {selectedRole && !showOtp && (
          <div style={{ background:panelBg, border:`1px solid ${border}`, borderRadius:20, padding:"clamp(20px,4vw,36px)", boxShadow:D?"0 4px 32px rgba(0,0,0,0.22)":"0 4px 24px rgba(13,31,60,0.08)", color:text, animation:"slideDown .3s ease" }}>

            <div style={{ display:"flex", alignItems:"center", gap:10, paddingBottom:20, marginBottom:24, borderBottom:`1px solid ${border}` }}>
              <div style={{ width:42, height:42, borderRadius:11, background:`${role.color}18`, border:`1.5px solid ${role.color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{role.icon}</div>
              <div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:16, color:role.color }}>{role.title} Account</div>
                <div style={{ fontSize:12, color:textMid }}>Fill in your details below to create your account.</div>
              </div>
              <button onClick={() => setSelectedRole(null)}
                style={{ marginLeft:"auto", fontSize:12, color:textSoft, background:"none", border:`1px solid ${border}`, borderRadius:8, padding:"5px 10px", cursor:"pointer", fontFamily:"inherit" }}>
                Change role
              </button>
            </div>

            {apiError && (
              <div style={{ marginBottom:20, padding:"11px 14px", borderRadius:10, background:"rgba(251,113,133,0.10)", border:"1px solid rgba(251,113,133,0.25)", fontSize:13, color:"#fda4af", fontWeight:500 }}>
                ⚠ {apiError}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* 
                SECURITY & UI FIX: Dummy fields intercept aggressive browser autofill algorithms. 
                Using `autoComplete="nope"` on the actual visible inputs blocks most extensions.
              */}
              <input type="email" style={{ display: "none" }} autoComplete="email" />
              <input type="password" style={{ display: "none" }} autoComplete="new-password" />

              <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ 
                  width: 60, height: 60, borderRadius: '50%', background: inputBg, 
                  border: `2px dashed ${border}`, display: 'flex', alignItems: 'center', 
                  justifyContent: 'center', overflow: 'hidden' 
                }}>
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Avatar Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <User size={24} color={textSoft} />
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: text, marginBottom: 4, cursor: 'pointer' }}>
                    Upload Profile Picture
                    <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                  </label>
                  <div style={{ fontSize: 11, color: textMid }}>JPG, PNG or GIF (Upto 5 MB) (Optional)</div>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"0 20px" }}>
                <Field label="Full Name" error={errors.fullName}>
                  <div style={{ position:"relative" }}>
                    <User size={15} style={iconStyle}/>
                    <input type="text" name="fullName" value={formData.fullName} onChange={handleChange}
                      autoComplete="nope"
                      placeholder="Jane Smith"
                      onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=errors.fullName?"#f87171":border}
                      style={inp(errors.fullName)}/>
                  </div>
                </Field>
                <Field label="Email Address" error={errors.email}>
                  <div style={{ position:"relative" }}>
                    <Mail size={15} style={iconStyle}/>
                    <input type="email" name="email" value={formData.email} onChange={handleChange}
                      autoComplete="nope"
                      placeholder="you@gmail.com"
                      onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=errors.email?"#f87171":border}
                      style={inp(errors.email)}/>
                  </div>
                </Field>
                <Field label="Phone Number" error={errors.phone}>
                  <div style={{ position:"relative" }}>
                    <Phone size={15} style={iconStyle}/>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                      autoComplete="nope"
                      placeholder="(555) 123-4567"
                      onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=errors.phone?"#f87171":border}
                      style={inp(errors.phone)}/>
                  </div>
                </Field>
                {selectedRole !== "driver" && (
                  <Field label="Company Name" error={errors.companyName}>
                    <div style={{ position:"relative" }}>
                      <Building2 size={15} style={iconStyle}/>
                      <input type="text" name="companyName" value={formData.companyName} onChange={handleChange}
                        autoComplete="nope"
                        placeholder="Acme Oversize LLC"
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=errors.companyName?"#f87171":border}
                        style={inp(errors.companyName)}/>
                    </div>
                  </Field>
                )}
              </div>

              {selectedRole === "driver" && (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:"0 20px" }}>
                  <Field label="Years of Experience (optional)" error={errors.experience}>
                    <div style={{ position:"relative" }}>
                      <Briefcase size={15} style={iconStyle}/>
                      <select name="experience" value={formData.experience} onChange={handleChange}
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=border}
                        style={{ ...inp(errors.experience), appearance:"none", paddingRight:12 }}>
                        <option value="">Select level</option>
                        <option value="new">New — Getting Started</option>
                        <option value="1-3">1–3 Years</option>
                        <option value="4-7">4–7 Years</option>
                        <option value="8+">8+ Years</option>
                      </select>
                    </div>
                  </Field>
                  <Field label="Primary State (optional)" error={errors.primaryState}>
                    <div style={{ position:"relative" }}>
                      <MapPin size={15} style={iconStyle}/>
                      <select name="primaryState" value={formData.primaryState} onChange={handleChange}
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=border}
                        style={{ ...inp(errors.primaryState), appearance:"none", paddingRight:12 }}>
                        <option value="">Select State</option>
                        {US_STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  </Field>
                  <Field label="Equipment Status (optional)" error={errors.equipmentStatus}>
                    <div style={{ position:"relative" }}>
                      <Wrench size={15} style={iconStyle}/>
                      <select name="equipmentStatus" value={formData.equipmentStatus} onChange={handleChange}
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=border}
                        style={{ ...inp(errors.equipmentStatus), appearance:"none", paddingRight:12 }}>
                        <option value="">Select status</option>
                        <option value="yes">Fully Equipped</option>
                        <option value="partial">Partially Equipped</option>
                        <option value="no">Need Equipment</option>
                      </select>
                    </div>
                  </Field>
                  <Field label="Vehicle Year (optional)" error={errors.vehicleYear}>
                    <div style={{ position:"relative" }}>
                      <Car size={15} style={iconStyle}/>
                      <select name="vehicleYear" value={formData.vehicleYear} onChange={handleChange}
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=errors.vehicleYear?"#f87171":border}
                        style={{ ...inp(errors.vehicleYear), appearance:"none", paddingRight:12 }}>
                        <option value="">Year</option>
                        {VEHICLE_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </Field>
                  <Field label="Make (optional)" error={errors.vehicleMake}>
                    <div style={{ position:"relative" }}>
                      <Wrench size={15} style={iconStyle}/>
                      <select name="vehicleMake" value={formData.vehicleMake}
                        onChange={e => { handleChange(e); setFormData(p => ({ ...p, vehicleModel:"" })); }}
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=errors.vehicleMake?"#f87171":border}
                        style={{ ...inp(errors.vehicleMake), appearance:"none", paddingRight:12 }}>
                        <option value="">Select Make</option>
                        {VEHICLE_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </Field>
                  <Field label="Model (optional)" error={errors.vehicleModel}>
                    <div style={{ position:"relative" }}>
                      <Car size={15} style={iconStyle}/>
                      {(VEHICLE_MODELS[formData.vehicleMake]) ? (
                        <select name="vehicleModel" value={formData.vehicleModel} onChange={handleChange}
                          onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=errors.vehicleModel?"#f87171":border}
                          style={{ ...inp(errors.vehicleModel), appearance:"none", paddingRight:12 }}>
                          <option value="">Select Model</option>
                          {VEHICLE_MODELS[formData.vehicleMake].map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      ) : (
                        <input type="text" name="vehicleModel" value={formData.vehicleModel} onChange={handleChange}
                          autoComplete="nope"
                          placeholder="Enter model (optional)"
                          onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=errors.vehicleModel?"#f87171":border}
                          style={inp(errors.vehicleModel)}/>
                      )}
                    </div>
                  </Field>
                  <Field label="Color">
                    <div style={{ position:"relative" }}>
                      <div style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", width:12, height:12, borderRadius:"50%", background:
                        formData.vehicleColor==="White"?"#fff":formData.vehicleColor==="Black"?"#111":formData.vehicleColor==="Silver"?"#c0c0c0":
                        formData.vehicleColor==="Gray"||formData.vehicleColor==="Dark Gray"?"#6b7280":formData.vehicleColor==="Red"?"#ef4444":
                        formData.vehicleColor==="Blue"?"#3b82f6":formData.vehicleColor==="Dark Blue"||formData.vehicleColor==="Navy"?"#1e3a5f":
                        formData.vehicleColor==="Green"?"#22c55e":formData.vehicleColor==="Yellow"?"#facc15":
                        formData.vehicleColor==="Orange"?"#f97316":formData.vehicleColor==="Beige"?"#f5f0e8":
                        formData.vehicleColor==="Brown"?"#92400e":formData.vehicleColor==="Gold"?"#f59e0b":"#9ca3af",
                        border:`1px solid ${border}`, pointerEvents:"none",
                      }}/>
                      <select name="vehicleColor" value={formData.vehicleColor} onChange={handleChange}
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=border}
                        style={{ ...inp(false), paddingLeft:34, appearance:"none", paddingRight:12 }}>
                        <option value="">Color (optional)</option>
                        {VEHICLE_COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </Field>
                  <Field label="Additional Details (optional)">
                    <div style={{ position:"relative" }}>
                      <Wrench size={15} style={{ ...iconStyle, top:14, transform:"none" }}/>
                      <textarea rows={2} name="vehicleNotes" value={formData.vehicleNotes||""} onChange={handleChange}
                        autoComplete="nope"
                        placeholder="Signs, lights, high-pole setup, any extras…"
                        style={{ ...inp(false), padding:"11px 11px 11px 38px", resize:"none", height:"auto", gridColumn:"1/-1" }}/>
                    </div>
                  </Field>
                </div>
              )}

              {selectedRole === "company" && (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"0 20px" }}>
                  <Field label="Fleet Size (optional)">
                    <div style={{ position:"relative" }}>
                      <Car size={15} style={iconStyle}/>
                      <select name="fleetSize" value={formData.fleetSize} onChange={handleChange}
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=border}
                        style={{ ...inp(false), appearance:"none", paddingRight:12 }}>
                        <option value="">How many loads/month?</option>
                        <option value="1-5">1–5 loads/month</option>
                        <option value="6-20">6–20 loads/month</option>
                        <option value="20+">20+ loads/month</option>
                      </select>
                    </div>
                  </Field>
                  <Field label="DOT / MC Number (optional)">
                    <div style={{ position:"relative" }}>
                      <Briefcase size={15} style={iconStyle}/>
                      <input type="text" name="dotNumber" value={formData.dotNumber} onChange={handleChange}
                        autoComplete="nope"
                        placeholder="DOT 1234567"
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=border}
                        style={inp(false)}/>
                    </div>
                  </Field>
                </div>
              )}

              {selectedRole === "dispatcher" && (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"0 20px" }}>
                  <Field label="Dispatch Experience (optional)">
                    <div style={{ position:"relative" }}>
                      <Briefcase size={15} style={iconStyle}/>
                      <select name="dispatchExp" value={formData.dispatchExp} onChange={handleChange}
                        onFocus={e=>e.target.style.borderColor=role.color} onBlur={e=>e.target.style.borderColor=border}
                        style={{ ...inp(false), appearance:"none", paddingRight:12 }}>
                        <option value="">Select level</option>
                        <option value="new">New to Dispatching</option>
                        <option value="1-3">1–3 Years</option>
                        <option value="4+">4+ Years</option>
                      </select>
                    </div>
                  </Field>
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"0 20px" }}>
                <Field label="Password" error={errors.password}>
                  <div style={{ position:"relative" }}>
                    <Lock size={15} style={iconStyle}/>
                    <input type={showPw?"text":"password"} value={password} placeholder="••••••••"
                      autoComplete="new-password"
                      onChange={e=>setPassword(e.target.value)}
                      onFocus={e=>{setPwFocused(true);e.target.style.borderColor=role.color}}
                      onBlur={e=>{setPwFocused(false);e.target.style.borderColor=errors.password?"#f87171":border}}
                      style={{ ...inp(errors.password), paddingRight:42 }}/>
                    <button type="button" onClick={()=>setShowPw(p=>!p)}
                      style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:textSoft }}>
                      {showPw?<EyeOff size={15}/>:<Eye size={15}/>}
                    </button>
                  </div>
                  {(pwFocused||password.length>0) && (
                    <div style={{ marginTop:8, display:"flex", flexDirection:"column", gap:3 }}>
                      {[
                        [validations.length,"At least 8 characters"],
                        [validations.uppercase,"One uppercase letter"],
                        [validations.lowercase,"One lowercase letter"],
                        [validations.number,"One number"],
                      ].map(([ok,txt]) => (
                        <div key={txt} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:ok?"#34d399":textSoft }}>
                          <Check size={11} style={{ opacity:ok?1:.4 }}/> {txt}
                        </div>
                      ))}
                    </div>
                  )}
                </Field>
                <Field label="Confirm Password" error={errors.confirmPassword}>
                  <div style={{ position:"relative" }}>
                    <Lock size={15} style={iconStyle}/>
                    <input type={showCpw?"text":"password"} value={confirmPassword} placeholder="••••••••"
                      onChange={e=>setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      onFocus={e=>e.target.style.borderColor=role.color}
                      onBlur={e=>e.target.style.borderColor=errors.confirmPassword?"#f87171":border}
                      style={{ ...inp(errors.confirmPassword), paddingRight:42 }}/>
                    <button type="button" onClick={()=>setShowCpw(p=>!p)}
                      style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:textSoft }}>
                      {showCpw?<EyeOff size={15}/>:<Eye size={15}/>}
                    </button>
                  </div>
                </Field>
              </div>

              <div style={{ marginBottom:20 }}>
                <Disclaimer variant="signup" checked={agreedToTerms} onChange={setAgreed}/>
                {errors.terms && <p style={{ marginTop:6, fontSize:12, color:"#fda4af", fontWeight:600 }}>{errors.terms}</p>}
              </div>

              <button type="submit" disabled={isLoading}
                style={{
                  width:"100%", padding:"15px", borderRadius:12, border:"none",
                  cursor:isLoading?"not-allowed":"pointer",
                  background:isLoading?`${role.color}55`:`linear-gradient(135deg,${amber},${amberL})`,
                  color:"#0d1f3c", fontWeight:800, fontSize:16,
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                  boxShadow:isLoading?"none":`0 4px 18px rgba(245,158,11,0.40)`,
                  fontFamily:"inherit", transition:"all .15s",
                }}
                onMouseEnter={e=>{ if(!isLoading) e.currentTarget.style.filter="brightness(1.06)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.filter="none"; }}>
                {isLoading
                  ? <><Loader2 size={18} style={{ animation:"spin 1s linear infinite" }}/> Creating your account…</>
                  : <>Create {role.title} Account <ChevronRight size={18}/></>}
              </button>

              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:16, fontSize:12, color:textSoft }}>
                <ShieldCheck size={13} style={{ color:"#22c55e" }}/>
                Secure, encrypted & industry-compliant
              </div>
            </form>
          </div>
        )}

        {/* ── OTP Screen ── */}
        {selectedRole && showOtp && (
          <div style={{ background:panelBg, border:`1px solid ${border}`, borderRadius:20, padding:"36px 32px", boxShadow:D?"0 4px 32px rgba(0,0,0,0.22)":"0 4px 24px rgba(13,31,60,0.08)", animation:"slideDown .3s ease" }}>
            <OtpScreen email={regEmail} onBack={() => setShowOtp(false)} colors={colors}/>
          </div>
        )}

        {/* ── Footer ── */}
        <p style={{ textAlign:"center", marginTop:24, fontSize:13, color:textMid }}>
          Already have an account?{" "}
          <Link to="/login" style={{ fontWeight:800, color:amber, textDecoration:"none" }}>Sign in →</Link>
        </p>
      </div>
    </div>
  );
}